// Schlüssel der Abfragen und die Regel, wann ein Echtzeit-Hinweis eine Abfrage neu lädt.
//
//   Schlüssel   ['action', '<modul>.<gegenstand>.<verb>', Eingabe]            (TanStack Query sortiert Objektschlüssel
//               ['action', …, Eingabe, 'infinite'] für geblätterte Listen      selbst – gleiche Eingabe, gleicher Schlüssel)
//   Hinweis     { event, refs, locationIds } ohne Daten (docs/API.md §5)
//   Regel       Standard 'module': jeder Hinweis, dessen Ereignisname mit dem Modul des Vertrags beginnt
//               ('stock.transfer.shipped' lädt alle Abfragen von 'stock.*' neu) – oder eine ausdrückliche Liste.
import type { AnyActionContract } from '@shared/contract'
import type { RealtimeHint } from './types'

export const ACTION_KEY = 'action'

type Named = string | { readonly name: string }
const nameOf = (value: Named): string => (typeof value === 'string' ? value : value.name)

/** Schlüssel einer Abfrage; ohne Eingabe der Präfix aller Abfragen dieser Aktion. */
export function actionQueryKey<C extends AnyActionContract>(contract: C | string, input?: unknown): readonly unknown[] {
  const name = nameOf(contract)
  return input === undefined ? [ACTION_KEY, name] : [ACTION_KEY, name, input]
}

export const moduleOf = (name: string): string => name.split('.')[0] ?? name

/**
 *   'module'            Hinweise aus dem Modul des Vertrags (Standard)
 *   '*'                 jeder Hinweis (Zähler, die von allem abhängen: Benachrichtigungen, Aufgaben)
 *   ['sales', itemSaved, 'stock.transfer']   Ereignis- oder Aktionsnamen bzw. deren Präfixe (Vertragsobjekte erlaubt)
 *   false               nie
 *   (hint) => boolean   eigene Entscheidung, z. B. nach Standort oder Datensatz
 */
export type RefreshRule = 'module' | '*' | false | readonly Named[] | ((hint: RealtimeHint) => boolean)

const startsWithSegment = (event: string, prefix: string): boolean =>
  event === prefix || event.startsWith(prefix.endsWith('.') ? prefix : `${prefix}.`)

/** Lädt dieser Hinweis eine Abfrage der Aktion `actionName` neu? */
export function hintMatches(rule: RefreshRule | undefined, actionName: string, hint: RealtimeHint): boolean {
  if (rule === false) return false
  if (rule === undefined || rule === 'module') return startsWithSegment(hint.event, moduleOf(actionName))
  if (rule === '*') return true
  if (typeof rule === 'function') return rule(hint)
  return rule.some((entry) => startsWithSegment(hint.event, nameOf(entry)))
}

/** Welche Abfragen nach einer Änderung neu geladen werden (useActionMutation `invalidate`). */
export type InvalidateTarget = 'module' | false | readonly Named[]

/** Trifft das Ziel die Abfrage der Aktion `queryAction`? `mutated` ist die ausgeführte Aktion. */
export function invalidationMatches(
  target: InvalidateTarget | undefined,
  mutated: string,
  queryAction: string,
): boolean {
  if (target === false) return false
  if (target === undefined || target === 'module') return moduleOf(queryAction) === moduleOf(mutated)
  return target.some((entry) => startsWithSegment(queryAction, nameOf(entry)))
}
